/**
 * Sales Instructions List Page
 * ISOLATED MODULE - Uses /api/sales-instructions/* endpoints only
 * Route: /sales/instructions/list
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus, Search, Home, MapPin, Loader2, FileText,
  CheckCircle, Clock, AlertCircle, Eye, ArrowLeft
} from "lucide-react";
import { API } from "@/App";
import SINewInstructionModal from "./SINewInstructionModal";

const statusColors = {
  DRAFT: "bg-slate-100 text-slate-700",
  READY_TO_APPROVE: "bg-amber-100 text-amber-700",
  APPROVED_TO_GO_LIVE: "bg-blue-100 text-blue-700",
  LIVE: "bg-green-100 text-green-700",
  WITHDRAWN: "bg-red-100 text-red-700"
};

const statusIcons = {
  DRAFT: Clock,
  READY_TO_APPROVE: AlertCircle,
  APPROVED_TO_GO_LIVE: FileText,
  LIVE: CheckCircle,
  WITHDRAWN: AlertCircle
};

const SIInstructionsListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [instructions, setInstructions] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNewModal, setShowNewModal] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchInstructions();
  }, [search, statusFilter]);

  const fetchInstructions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(
        `${API}/sales-instructions/instructions?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setInstructions(response.data.instructions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching instructions:", error);
      toast.error("Failed to load instructions");
    } finally {
      setLoading(false);
    }
  };

  const handleInstructionCreated = (instructionId) => {
    setShowNewModal(false);
    toast.success("Instruction created successfully");
    navigate(`/sales/instructions/${instructionId}`);
  };

  const formatPrice = (amount) => {
    if (!amount) return "—";
    return `£${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/sales")}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sales
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Sales Instructions</h1>
                <p className="text-sm text-slate-500">Manage property instructions and marketing</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowNewModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Instruction
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px] max-w-md relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by address, postcode..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="READY_TO_APPROVE">Ready to Approve</SelectItem>
                  <SelectItem value="APPROVED_TO_GO_LIVE">Approved</SelectItem>
                  <SelectItem value="LIVE">Live</SelectItem>
                  <SelectItem value="WITHDRAWN">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-sm text-slate-500">
                {total} instruction{total !== 1 ? "s" : ""}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : instructions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                <Home className="w-12 h-12 mb-4 text-slate-300" />
                <p className="text-lg font-medium">No instructions found</p>
                <p className="text-sm">Create your first instruction to get started</p>
                <Button 
                  onClick={() => setShowNewModal(true)}
                  className="mt-4"
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Instruction
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instructions.map((inst) => {
                    const StatusIcon = statusIcons[inst.status] || Clock;
                    return (
                      <TableRow 
                        key={inst.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/sales/instructions/${inst.id}`)}
                      >
                        <TableCell>
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Home className="w-5 h-5 text-slate-500" />
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">
                                {inst.address_line_1}
                              </div>
                              <div className="text-sm text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {inst.town}, {inst.postcode}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {formatPrice(inst.price_amount)}
                          </div>
                          {inst.price_type && (
                            <div className="text-xs text-slate-500">
                              {inst.price_type.replace(/_/g, " ")}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {inst.property_type || "—"}
                          </div>
                          {inst.bedrooms && (
                            <div className="text-xs text-slate-500">
                              {inst.bedrooms} bed • {inst.bathrooms || 0} bath
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[inst.status]} flex items-center gap-1 w-fit`}>
                            <StatusIcon className="w-3 h-3" />
                            {inst.status.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {formatDate(inst.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/sales/instructions/${inst.id}`);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Instruction Modal */}
      <SINewInstructionModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={handleInstructionCreated}
      />
    </div>
  );
};

export default SIInstructionsListPage;
